import { useEffect } from 'react';
import { Tenant } from '@99designs/marketing-ui';
import { trackEvent } from '@99designs/tracking';
import { ContestLayout } from '../components/ContestLayout';
import { FixedPriceProjectLayout } from '../components/FixedPriceProjectLayout';
import { ProductGroupType } from '../types';
import { useRelatedProductsConfig } from '../util/useRelatedProductsConfig';
import type { Design } from '@99designs/feature-design-inspiration';
import type { CurrencyCode } from '@99designs/i18n';
import type { ZendeskLanguage } from '@99designs/routing';
import type {
    ContestProduct,
    FixedPriceProjectProduct,
    ProductGroup,
    ProductGroupKey,
    RelatedProduct,
} from '../types';

export type ProductDetailsContainerProps = {
    productGroup: ProductGroup;
    products: ContestProduct[] | FixedPriceProjectProduct[];
    designInspiration: Design[] | null;
    productGroupKey: ProductGroupKey;
    relatedProducts: RelatedProduct[] | null;
    currency: CurrencyCode;
    lang: ZendeskLanguage;
    tenant: Tenant;
    anonymousUserId?: string;
};

export function ProductDetailsContainer({
    productGroup,
    products,
    designInspiration,
    productGroupKey,
    relatedProducts,
    currency,
    lang,
    tenant,
    anonymousUserId,
}: ProductDetailsContainerProps) {
    useEffect(() => {
        trackEvent('Viewed Product Details Page', {
            type: productGroup.attributes.type,
            productGroupKey: productGroupKey,
        });
    }, [productGroup.attributes.type, productGroupKey]);

    const crossPlatformTrackingParams = anonymousUserId && `vistaprint_aid=${anonymousUserId}`;

    const relatedProductsFromConfig: RelatedProduct[] = useRelatedProductsConfig(
        relatedProducts,
        currency
    );

    const inspiration =
        designInspiration != null && designInspiration.length > 0
            ? {
                  designOutcome: productGroup.attributes.designOutcome,
                  categoryKeys: [productGroup.designInspirationCategoryKey],
                  designs: designInspiration,
              }
            : undefined;

    switch (productGroup.attributes.type) {
        case ProductGroupType.Contest:
            return (
                <ContestLayout
                    productGroup={productGroup}
                    products={products as ContestProduct[]}
                    inspiration={inspiration}
                    relatedProducts={relatedProductsFromConfig}
                    lang={lang}
                    tenant={tenant}
                    crossPlatformTrackingParams={crossPlatformTrackingParams}
                />
            );
        case ProductGroupType.FixedPrice:
            return (
                <FixedPriceProjectLayout
                    productGroup={productGroup}
                    products={products as FixedPriceProjectProduct[]}
                    inspiration={inspiration}
                    relatedProducts={relatedProductsFromConfig}
                    lang={lang}
                    tenant={tenant}
                    crossPlatformTrackingParams={crossPlatformTrackingParams}
                />
            );
        default:
            return null;
    }
}
